import type { SuretyAccountId } from "@inrev/common";
import {
	useAdminFetchLatestSuretyAccountUnderwriting,
	useAdminUnderwrite,
} from "@portal/api/account/admin";
import { type FC, useEffect, useRef } from "react";
import { useReportLoadError } from "../features/load-errors";
import { unableToLoadMessage } from "../patterns/retryable-load-error";
import { Button, ClickableText, Icon, Text } from "../primitives";

const headerBarClassName =
	"flex w-full h-[36px] items-center justify-center space-x-[24px] bg-surface-neutral shadow-md border-b border-b-bc-default";

export interface UpdateUnderwritingProps {
	accountId: SuretyAccountId;
}

export const UpdateUnderwriting: FC<UpdateUnderwritingProps> = ({ accountId }) => {
	const {
		updateUnderwriting,
		updateUnderwritingIsLoading,
		updateUnderwritingSuccess,
		resetUnderwrite,
	} = useAdminUnderwrite(accountId, "contract");

	const {
		latestUnderwriting,
		latestUnderwritingError,
		latestUnderwritingIsFetching,
		latestUnderwritingIsLoading,
		refetchLatestUnderwriting,
	} = useAdminFetchLatestSuretyAccountUnderwriting(accountId);

	useReportLoadError(latestUnderwritingError);

	const isStale = !latestUnderwritingIsLoading && !!latestUnderwriting?.stale;

	const prevIsStaleRef = useRef(isStale);
	useEffect(() => {
		const wasStale = prevIsStaleRef.current;
		prevIsStaleRef.current = isStale;
		if (isStale && !wasStale && updateUnderwritingSuccess) {
			resetUnderwrite();
		}
	}, [isStale, updateUnderwritingSuccess, resetUnderwrite]);

	if (latestUnderwritingError) {
		return (
			<div data-testid="update-underwriting-load-error" className={headerBarClassName}>
				<Text type="caption" color="secondary">
					{unableToLoadMessage("account underwriting")}
				</Text>{" "}
				<ClickableText
					type="caption"
					color="link"
					onClick={() => refetchLatestUnderwriting()}
					disabled={latestUnderwritingIsFetching}
				>
					{latestUnderwritingIsFetching ? "Retrying…" : "Retry"}
				</ClickableText>
			</div>
		);
	}

	const show = isStale && !updateUnderwritingSuccess;

	return (
		<div
			data-testid="update-underwriting-wrapper"
			aria-hidden={!show}
			className="overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out"
			style={{
				maxHeight: show ? "36px" : "0px",
				opacity: show ? 1 : 0,
			}}
		>
			<div className={headerBarClassName}>
				<Text data-testid="update-underwriting-message" type="caption" color="primary">
					Updates were made to this account that may impact underwriting
				</Text>
				<Button
					data-testid="run-model-button"
					color="primary"
					size="sm"
					onClick={() => updateUnderwriting()}
					disabled={!show}
					loading={updateUnderwritingIsLoading}
					leadingIcon={<Icon type="arrow-path" color="inverse" size="micro" />}
				>
					Run Model
				</Button>
			</div>
		</div>
	);
};
